import { getState } from '/bb/state.js';
import {
  FOCUS_BY_CLICK_MAX_DT_MS,
  FOCUS_BY_TAB_MAX_DT_MS,
  AUTOFILL_PROBE_DELAY_MS,
  AUTOFILL_ANIMATION_NAME,
} from '/bb/constants.js';
import {
  currentPageKey,
  isAutocompleteEligibleField,
  isTextAutocompleteField,
  pageFormFieldKey,
  resolveField,
} from '/bb/identifier.js';
import {
  discardAutocompleteAcceptedValue,
  finalizeAutocompleteEdit,
  hasAutocompleteAcceptedValue,
  recordAutocompleteAcceptance,
  startAutocompleteBatch,
} from '/bb/autocomplete.js';
import {
  ensureCurrentPage,
  getOrCreateField,
  getOrCreateForm,
  markFormFocusIn,
  markFormFocusOut,
  elementContains,
} from '/bb/helpers.js';
import {
  finalizeDateCorrectionSession,
  peekDateCorrectionSession,
  restoreDateCorrectionSession,
  resetDateCorrectionBaseline,
  startDateCorrectionSession,
} from '/bb/listeners/input.js';

const styleProbes = new Map();

function probeAppearanceAutofill(el, fld) {
  if (typeof window === 'undefined') return;
  try {
    if (styleProbes.has(el)) return;
    const handle = setTimeout(() => {
      styleProbes.delete(el);
      if (fld.autocomplete && fld.autocomplete.detected) return;
      try {
        const ap = window.getComputedStyle(el, null).getPropertyValue('appearance');
        if (ap === 'menulist-button') {
          fld.autocomplete.detected = true;
          fld.runtime.autocompleteDetectedThisFocus = true;
          recordAutocompleteAcceptance(fld, el);
        }
      } catch (err) {
        /* CSP / detached node — ignore */
      }
    }, AUTOFILL_PROBE_DELAY_MS);
    styleProbes.set(el, handle);
  } catch (err) {
    /* ignore */
  }
}

function focusInHandler(e) {
  const s = getState();
  const t = Date.now();
  const field = resolveField(e.target);
  if (!field) return;
  const pageKey = ensureCurrentPage();
  const form = getOrCreateForm(pageKey, field.formId);
  const fld = getOrCreateField(pageKey, field.formId, field.fieldName, field.type, field.fieldCategories);
  const rt = fld.runtime;
  const isText = !!fld.keystroke;

  if (rt.firstFocusAt === 0) {
    rt.firstFocusAt = t;
  } else {
    if (isText) fld.revisit_count += 1;
  }

  if (rt.lastFocusOutAt > 0 && rt.inputsThisFocus === 0 && isText) {
    fld.abandonment_and_return_count += 1;
  }

  if (isText) {
    if (rt.hoverSince > 0) {
      const hoverDelta = Math.max(0, t - rt.hoverSince);
      rt.hoverAccumMs += hoverDelta;
      rt.hoverSince = 0;
    }
    if (rt.hoverAccumMs > 0) fld.pause_before_focus_ms = rt.hoverAccumMs;
    rt.hoverAccumMs = 0;

    const last = s.transient.lastPointerDown;
    const lastK = s.transient.lastKey;
    const wasClick =
      last && last.target && t - last.t <= FOCUS_BY_CLICK_MAX_DT_MS && elementContains(field.element, last.target);
    const wasTab = lastK && lastK.wasTab && t - lastK.t <= FOCUS_BY_TAB_MAX_DT_MS;
    if (wasClick) fld.focus_by_click_count += 1;
    else if (wasTab) fld.focus_by_tab_count += 1;
  }

  rt.lastFocusInAt = t;
  if (isText) {
    rt.lastInputValueLength = typeof e.target.value === 'string' ? e.target.value.length : 0;
    rt.valueAtFocus = typeof e.target.value === 'string' ? e.target.value : null;
  }
  rt.changedAwayFromFocusValue = false;
  rt.returnedToOriginalRecordedThisFocus = false;
  rt.inputsThisFocus = 0;
  rt.pasteThisFocus = false;
  rt.focusSessions += 1;
  rt.currentlyFocused = true;

  startDateCorrectionSession(field, fld, form, e.target);

  markFormFocusIn(form, t);

  if (isText) {
    const autocompleteEligible = isAutocompleteEligibleField(field);
    // Safari Contact AutoFill can accept a sibling date field while it is not
    // focused. Keep that value until its next blur so edits_after can compare
    // the final user-edited value with the accepted one. It is only a value
    // baseline, though: every new focus starts a new acceptance opportunity.
    if (!hasAutocompleteAcceptedValue(e.target)) {
      discardAutocompleteAcceptedValue(e.target);
    }
    rt.autocompleteAcceptedRecorded = false;
    rt.autocompleteDetectedThisFocus = false;
    if (isTextAutocompleteField(field) && autocompleteEligible) probeAppearanceAutofill(e.target, fld);
  }
}

function focusOutHandler(e) {
  const t = Date.now();
  const field = resolveField(e.target);
  if (!field) return;
  const pageKey = ensureCurrentPage();
  const form = getOrCreateForm(pageKey, field.formId);
  const fld = getOrCreateField(pageKey, field.formId, field.fieldName, field.type, field.fieldCategories);
  const rt = fld.runtime;
  if (rt.lastFocusInAt > 0 && fld.focus_duration_ms != null) {
    fld.focus_duration_ms += Math.max(0, t - rt.lastFocusInAt);
  }
  if (fld.keystroke && rt.inputsThisFocus === 0 && rt.lastInputValueLength === 0) {
    fld.empty_focus_count += 1;
  }
  if (fld.keystroke && typeof e.target.value === 'string') {
    fld.value_length = e.target.value.length;
    rt.lastInputValueLength = fld.value_length;
  }
  finalizeReturnedToOriginal(fld, e.target);
  finalizeDateCorrectionSession(field, fld, form, e.target, t);
  finalizeAutocompleteEdit(fld, e.target);
  rt.lastFocusOutAt = t;
  rt.currentlyFocused = false;
  if (rt.backspaceBurstStarted) {
    const burst = rt.backspaceBurstLen;
    rt.backspaceBurstStarted = false;
    rt.backspaceBurstLen = 0;
    if (burst > 0 && fld.corrections) {
      fld.corrections._bursts.n += 1;
      const delta = burst - fld.corrections._bursts.mean;
      fld.corrections._bursts.mean += delta / fld.corrections._bursts.n;
      fld.corrections._bursts.m2 += delta * (burst - fld.corrections._bursts.mean);
      if (burst < fld.corrections._bursts.min) fld.corrections._bursts.min = burst;
      if (burst > fld.corrections._bursts.max) fld.corrections._bursts.max = burst;
    }
  }

  const relatedField = e.relatedTarget ? resolveField(e.relatedTarget) : null;
  const stillInForm = relatedField && relatedField.formId === field.formId;
  if (!stillInForm) markFormFocusOut(form, t);
}

function animationStartHandler(e) {
  if (!e || e.animationName !== AUTOFILL_ANIMATION_NAME) return;
  const field = resolveField(e.target);
  if (!field || !isAutocompleteEligibleField(field)) return;
  const pageKey = ensureCurrentPage();
  const fld = getOrCreateField(pageKey, field.formId, field.fieldName, field.type, field.fieldCategories);
  if (fld.autocomplete) {
    // For semantic date/month/number fields this animation is the only trusted
    // browser-autofill signal; native picker input events are manual edits.
    fld.autocomplete.detected = true;
    fld.runtime.autocompleteDetectedThisFocus = true;
    if (recordAutocompleteAcceptance(fld, e.target)) {
      resetDateCorrectionBaseline(field, fld, getOrCreateForm(pageKey, field.formId), e.target, Date.now());
      if (isTextAutocompleteField(field)) startAutocompleteBatch(field.formId, e.target);
    }
  }
}

export function finalizeActiveFieldSession() {
  if (typeof document === 'undefined') return;

  const active = document.activeElement;
  const field = resolveField(active);
  if (!field) return;

  const pageKey = ensureCurrentPage();
  const form = getOrCreateForm(pageKey, field.formId);
  const fld = getOrCreateField(pageKey, field.formId, field.fieldName, field.type, field.fieldCategories);
  if (fld.keystroke && typeof active.value === 'string') {
    fld.value_length = active.value.length;
    fld.runtime.lastInputValueLength = fld.value_length;
  }
  finalizeReturnedToOriginal(fld, active);
  finalizeDateCorrectionSession(field, fld, form, active);
}

function copyPlain(value) {
  if (Array.isArray(value)) return value.map(copyPlain);
  if (value && typeof value === 'object') {
    const out = {};
    const keys = Object.keys(value);
    for (let i = 0; i < keys.length; i++) out[keys[i]] = copyPlain(value[keys[i]]);
    return out;
  }
  return value;
}

export function previewActiveFieldSession(read) {
  if (typeof document === 'undefined') return read();
  const active = document.activeElement;
  const field = resolveField(active);
  const s = getState();
  if (
    !field ||
    !s.currentPageKey ||
    s.currentPageKey !== currentPageKey() ||
    !s.fields.has(pageFormFieldKey(s.currentPageKey, field.formId, field.fieldName))
  ) {
    return read();
  }

  const pageEnteredAt = s.currentPageEnteredAt;
  const pageKey = ensureCurrentPage();
  const form = getOrCreateForm(pageKey, field.formId);
  const fld = getOrCreateField(pageKey, field.formId, field.fieldName, field.type, field.fieldCategories);
  const saved = saveOwn(fld, ['value_length', 'returned_to_original_count', 'corrections'])
    .concat(saveOwn(fld.runtime, ['lastInputValueLength', 'returnedToOriginalRecordedThisFocus']))
    .concat(saveOwn(form, ['corrections']));
  const dateSession = peekDateCorrectionSession(active);
  finalizeActiveFieldSession();
  try {
    return read();
  } finally {
    s.currentPageEnteredAt = pageEnteredAt;
    restoreOwn(saved);
    restoreDateCorrectionSession(active, dateSession);
  }
}

function saveOwn(obj, keys) {
  const saved = [];
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    saved.push({ obj, key, own: Object.prototype.hasOwnProperty.call(obj, key), value: copyPlain(obj[key]) });
  }
  return saved;
}

function restoreOwn(saved) {
  for (let i = 0; i < saved.length; i++) {
    const item = saved[i];
    if (item.own) item.obj[item.key] = item.value;
    else delete item.obj[item.key];
  }
}

export function syncActiveField(t = Date.now()) {
  if (typeof document === 'undefined') return;

  const active = document.activeElement;
  const field = resolveField(active);
  if (!field) return;

  const pageKey = ensureCurrentPage();
  const form = getOrCreateForm(pageKey, field.formId);
  const fld = getOrCreateField(pageKey, field.formId, field.fieldName, field.type, field.fieldCategories);
  const rt = fld.runtime;
  const isText = !!fld.keystroke;

  if (rt.firstFocusAt === 0) rt.firstFocusAt = t;
  rt.lastFocusInAt = t;
  if (isText) {
    rt.lastInputValueLength = typeof active.value === 'string' ? active.value.length : rt.lastInputValueLength;
    if (typeof active.value === 'string') fld.value_length = active.value.length;
    rt.valueAtFocus = typeof active.value === 'string' ? active.value : rt.valueAtFocus;
  }
  rt.currentlyFocused = true;

  startDateCorrectionSession(field, fld, form, active);

  markFormFocusIn(form, t, { countVisit: false });
  if (form.time_on_form_ms > 0 && form.runtime.firstFocusAt === t) {
    form.runtime.firstFocusAt = t - form.time_on_form_ms;
  }
}

function finalizeReturnedToOriginal(fld, target) {
  const rt = fld.runtime;

  if (!fld.keystroke) return;
  const currentValue = typeof target.value === 'string' ? target.value : null;
  if (rt.returnedToOriginalRecordedThisFocus) return;
  if (rt.focusSessions <= 1) return;
  if (rt.inputsThisFocus <= 0) return;
  if (!rt.changedAwayFromFocusValue) return;
  if (rt.valueAtFocus === null || rt.valueAtFocus === '') return;
  if (currentValue !== rt.valueAtFocus) return;

  fld.returned_to_original_count += 1;
  rt.returnedToOriginalRecordedThisFocus = true;
}

const STYLE_ID = 'rs-bb-autofill-style';

function injectAutofillStyle() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent =
    '@keyframes ' +
    AUTOFILL_ANIMATION_NAME +
    ' { from { } to { } } input:-webkit-autofill { animation-name: ' +
    AUTOFILL_ANIMATION_NAME +
    '; animation-duration: 1ms; }';
  try {
    (document.head || document.documentElement).appendChild(style);
  } catch (err) {
    /* ignore */
  }
}

let attached = false;

export function attach() {
  if (attached || typeof document === 'undefined') return;
  document.addEventListener('focusin', focusInHandler, { passive: true, capture: true });
  document.addEventListener('focusout', focusOutHandler, { passive: true, capture: true });
  document.addEventListener('animationstart', animationStartHandler, { passive: true, capture: true });
  injectAutofillStyle();
  attached = true;
}

export function detach() {
  if (!attached || typeof document === 'undefined') return;
  document.removeEventListener('focusin', focusInHandler, true);
  document.removeEventListener('focusout', focusOutHandler, true);
  document.removeEventListener('animationstart', animationStartHandler, true);
  for (const handle of styleProbes.values()) clearTimeout(handle);
  styleProbes.clear();
  attached = false;
}
